import { Error } from '../../../../ErrorHandling';
import { Resource } from '../../../../Resource/Types';
import { TPlainTextKey } from '../../../../Translations';

export interface ImportParticipantsOut {
  import_participant: {
    participants: ParticipantImportFileRow[];
  };
}

export interface ParticipantImportFileRow {
  email: string;
  firstname: string;
  lastname: string;
}

export interface ParticipantImportFileData {
  fileName: string;
  rows: ParticipantImportFileRow[];
}

interface ParticipantBase {
  email: string;
  firstName: string;
  lastName: string;
}

export interface FailedParticipant extends ParticipantBase {
  errorMessage: string;
  kind: 'failed';
}

export interface SuccessfulParticipant extends ParticipantBase {
  kind: 'successful';
  id: number;
}

export interface ImportedParticipants {
  failedParticipants: Array<FailedParticipant>;
  successfulParticipants: Array<SuccessfulParticipant>;
  formattingIssue: Array<FailedParticipant>;
  participantsOfDifferentOrg: Array<FailedParticipant>;
  participantsOfSameOrg: Array<FailedParticipant>;
}

export type ImportedParticipantsResource = Resource<ImportedParticipants>;

export interface DataParseError {
  kind: 'verify' | 'decoder';
  message: TPlainTextKey;
}

interface Waiting {
  kind: 'waiting';
}

interface Loading {
  kind: 'loading';
}

interface UploadingReady {
  kind: 'uploading-ready';
}

interface FileSelected {
  kind: 'file-selected';
  file: File;
}

interface FileSelectSucceeded {
  kind: 'file-select-succeeded';
  filename: string;
  initialCount: number;
  rows: ParticipantImportFileRow[];
}

interface FileSelectFailed {
  kind: 'file-select-failed';
  errorMessage: TPlainTextKey;
}

interface Submit {
  kind: 'submit';
  filename: string;
  initialCount: number;
  rows: ParticipantImportFileRow[];
}

export interface SubmitSucceeded {
  kind: 'submit-succeeded';
  filename: string;
  initialCount: number;
  importedParticipantsResource: ImportedParticipantsResource;
}

interface ViewReport {
  kind: 'view-report';
  filename: string;
  initialCount: number;
  importedParticipantsResource: ImportedParticipantsResource;
  view: ViewReportAction;
}

interface ViewSuccessReport {
  kind: 'view-report';
  filename: string;
  initialCount: number;
  successfulParticipants: SuccessfulParticipant[];
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (): Loading => ({
  kind: 'loading',
});

export const uploadingReady = (): UploadingReady => ({
  kind: 'uploading-ready',
});

export const fileSelected = (file: File): FileSelected => ({
  kind: 'file-selected',
  file,
});

export const fileSelectSucceeded = (
  rows: ParticipantImportFileRow[],
  filename: string,
): FileSelectSucceeded => ({
  kind: 'file-select-succeeded',
  rows,
  filename,
  initialCount: rows.length,
});

export const fileSelectFailed = (errorMessage: TPlainTextKey): FileSelectFailed => ({
  kind: 'file-select-failed',
  errorMessage,
});

export const submit = (rows: ParticipantImportFileRow[], filename: string): Submit => ({
  kind: 'submit',
  rows,
  filename,
  initialCount: rows.length,
});

export const submitSucceeded = (
  state: Submit | ViewReport,
  importedParticipantsResource: ImportedParticipantsResource,
): SubmitSucceeded => ({
  kind: 'submit-succeeded',
  initialCount: state.initialCount,
  filename: state.filename,
  importedParticipantsResource,
});

export const viewReport = (state: SubmitSucceeded, view: ViewReportAction): ViewReport => ({
  kind: 'view-report',
  filename: state.filename,
  initialCount: state.initialCount,
  importedParticipantsResource: state.importedParticipantsResource,
  view,
});

export const viewSuccessReport = (state: SubmitSucceeded): ViewSuccessReport => ({
  kind: 'view-report',
  filename: state.filename,
  initialCount: state.initialCount,
  successfulParticipants: state.importedParticipantsResource.payload.successfulParticipants,
});

export type ViewReportAction =
  | 'full-view'
  | 'success-view'
  | 'already-exist-in-same-org'
  | 'already-exist-in-diff-org'
  | 'formatting-issue'
  | 'import-failed';

export type State =
  | Waiting
  | Loading
  | UploadingReady
  | FileSelected
  | FileSelectSucceeded
  | FileSelectFailed
  | Error
  | Submit
  | SubmitSucceeded
  | ViewReport;
